<template>
  <div>
    <slot
      :price="price"
      :applied-reductions="appliedReductions"
      :to-currency="toCurrency"
    >
      <slot name="relative-reductions"> </slot>
      <div
        :class="{
          'md:mt-2': showBadge,
          'text-xl': size === 'xl',
          'text-sm': size === 'sm',
          'text-md': size === 'md',
          'text-md leading-[14px] lg:text-base lg:leading-[19px]':
            size === 'md' && !isPdp,
          'font-bold': type === 'loud',
          'text-red': showPriceReduction && appliedReductions.length,
          'flex flex-col-reverse': verticalAlign,
          'flex flex-col': isPdp,
        }"
      >
        <div class="flex items-baseline justify-start space-x-2">
          <span
            v-if="showPriceReduction && totalReductions.absoluteWithTax"
            :class="{
              'text-sm': size === 'md' && !isPdp,
              'text-base': size === 'xl' && isPdp,
            }"
            class="font-medium text-black line-through"
          >
            {{ toCurrency(priceWithTax + totalReductions.absoluteWithTax) }}
          </span>

          <div
            class="flex"
            :class="{
              'justify-end md:justify-start': isPdp,
              'justify-end': !isPdp,
            }"
          >
            <ProductDetailsBadges
              v-if="product"
              :applied-reductions="appliedReductions"
              :product="product"
              :show-sale-badge="showBadge && !!totalReductions"
              :show-discount-as-absolute-price="showDiscountAsAbsolutePrice"
            />
          </div>
        </div>
        <span
          :data-qa="isPdp ? 'product-price' : 'price'"
          data-test-id="product-price"
        >
          {{ toCurrency(priceWithTax) + ' ' }}
        </span>
        <span
          class="text-sm text-black"
          :class="{ 'flex flex-col md:inline': isPdp }"
        >
          <template v-if="showPriceReduction && appliedReductions.length">
            <span>
              <ProductLowestPriorPrice :lowest-prior-price="lowestPriorPrice" />
              <span v-if="showLowestPriorPriceSeparator" class="mx-1"> | </span>
            </span>
          </template>

          <span>
            <slot name="tax-info">
              <span v-if="showTaxInfo">
                {{ $t('price.including_vat')
                }}<span v-if="showShippingCostHint">, </span>
              </span>
            </slot>
            <slot name="shipping-costs">
              <ShippingCostHint v-if="showShippingCostHint" />
            </slot>

            <slot name="more-details-button">
              <button
                v-if="showMoreDetailsButton"
                class="flex min-h-0 p-0 underline"
                @click="$emit('click:more-details')"
              >
                {{ $t('price.more_details_button') }}
              </button>
            </slot>
          </span>
        </span>
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
import type {
  AppliedReduction,
  LowestPriorPrice,
  Price,
  Product,
} from '@scayle/storefront-nuxt'
import { toCurrency } from '#layers/base/utils/price'

const props = withDefaults(
  defineProps<{
    price: Price
    product: Product
    lowestPriorPrice?: LowestPriorPrice
    appliedReductions?: AppliedReduction[]
    showTaxInfo?: boolean
    showPriceReduction?: boolean
    showPriceReductionBadge?: boolean
    showDiscountAsAbsolutePrice?: boolean
    showShippingCostHint?: boolean
    size?: 'xl' | 'md' | 'sm'
    type?: 'loud' | 'whisper'
    isPdp?: boolean
    verticalAlign?: boolean
    showMoreDetailsButton?: boolean
  }>(),
  {
    lowestPriorPrice: undefined,
    appliedReductions: () => [],
    showTaxInfo: false,
    showPriceReduction: true,
    showPriceReductionBadge: false,
    showDiscountAsAbsolutePrice: false,
    showShippingCostHint: false,
    size: 'xl',
    type: 'loud',
    isPdp: false,
    verticalAlign: false,
    showMoreDetailsButton: false,
  },
)

defineEmits(['click:more-details'])

const slots = useSlots()

const priceWithTax = computed(() => props.price?.withTax)
const showBadge = computed(
  () => props.appliedReductions && props.showPriceReductionBadge,
)

const showLowestPriorPriceSeparator = computed(() => {
  const hasLowestPriorPrice = !!props.lowestPriorPrice?.withTax
  const hasConditions =
    slots['tax-info'] ||
    slots['shipping-costs'] ||
    props.showTaxInfo ||
    props.showShippingCostHint

  return hasLowestPriorPrice && hasConditions
})

const totalReductions = computed(() =>
  getTotalAppliedReductions(props.price, props.appliedReductions),
)

const getTotalAppliedReductions = (
  price: Price,
  appliedReductions: AppliedReduction[],
) => {
  const total = { absoluteWithTax: 0, relative: 0.0 }
  if (price) {
    appliedReductions.forEach((reduction) => {
      total.absoluteWithTax += reduction.amount.absoluteWithTax
      total.relative += reduction.amount.relative
    })
  }
  return total
}
</script>
